import React from 'react';
import type { LucideIcon } from 'lucide-react';


interface StatsCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  color: 'blue' | 'green' | 'yellow' | 'red' | 'purple';
  trend?: {
    value: number;
    isPositive: boolean;
    label?: string; // defaults to "from last week"
  };
  // Plain caption shown instead of a trend (e.g. "0 deployed", "This month").
  subtitle?: string;
}

const StatsCard: React.FC<StatsCardProps> = ({ title, value, icon: Icon, color, trend, subtitle }) => {
  const iconClasses = {
    blue: 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-blue-200',
    green: 'bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-emerald-200',
    yellow: 'bg-gradient-to-br from-amber-500 to-amber-600 shadow-amber-200',
    red: 'bg-gradient-to-br from-rose-500 to-rose-600 shadow-rose-200',
    purple: 'bg-gradient-to-br from-violet-500 to-violet-600 shadow-violet-200',
  };
  const topBarClasses = {
    blue: 'bg-blue-500',
    green: 'bg-emerald-500',
    yellow: 'bg-amber-500',
    red: 'bg-rose-500',
    purple: 'bg-violet-500',
  };

  return (
    <div className="relative bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-1 ${topBarClasses[color]}`} />
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {trend ? (
            <p className={`text-sm mt-1 ${trend.isPositive ? 'text-emerald-600' : 'text-rose-600'}`}>
              {trend.isPositive ? '+' : ''}{trend.value}% {trend.label || 'from last week'}
            </p>
          ) : subtitle ? (
            <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
          ) : null}
        </div>
        <div className={`p-3 rounded-lg shadow-md ${iconClasses[color]}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
};

export default StatsCard;